import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Link2, RefreshCw, Unplug, CheckCircle2, AlertTriangle, FileSpreadsheet, Copy, ShieldCheck, Globe, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABEL = { name: "Name", phone: "Phone", alt_phone: "Alt phone", email: "Email", locality: "Locality", occupation: "Occupation", course_interest: "Course interest", preferred_timings: "Preferred timings", source_details: "Source details", source_lead_id: "External lead ID", notes: "Notes" };
const MAP_FIELDS = ["name", "phone", "email", "locality", "course_interest", "source_lead_id", "notes"];

function ColumnMapper({ preview, mapping, setMapping }) {
  const headers = [...new Set((preview.headers || []).filter((h) => h && h.trim()))];
  return (
    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
      {MAP_FIELDS.map((f) => (
        <div key={f}>
          <Label className="text-xs">{FIELD_LABEL[f]}{f === "name" ? " *" : ""}</Label>
          <Select value={mapping[f] || "none"} onValueChange={(v) => setMapping((m) => { const c = { ...m }; if (v === "none") delete c[f]; else c[f] = v; return c; })}>
            <SelectTrigger data-testid={`gs-map-${f}`}><SelectValue placeholder="— skip —" /></SelectTrigger>
            <SelectContent><SelectItem value="none">— skip —</SelectItem>{headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

function ExtraTabs({ status }) {
  const qc = useQueryClient();
  const isSA = status?.config?.mode === "service_account";
  const tabs = status?.config?.extra_tabs || [];
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("");
  const [label, setLabel] = useState("");
  const [headerRow, setHeaderRow] = useState("1");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const reset = () => { setOpen(false); setTab(""); setLabel(""); setHeaderRow("1"); setPreview(null); setMapping({}); };
  const doPreview = async () => {
    setBusy(true);
    try { const { data } = await api.post("/integrations/gsheets/tabs/preview", { tab, header_row: Number(headerRow) || 1 }); setPreview(data); setMapping(data.suggested_mapping || {}); toast.success(`Found ${data.total_rows} rows`); }
    catch (e) { setPreview(null); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const add = useMutation({
    mutationFn: async () => (await api.post("/integrations/gsheets/tabs", { tab, label, header_row: Number(headerRow) || 1, mapping })).data,
    onSuccess: () => { toast.success("Tab added — it will sync with the main tab"); reset(); qc.invalidateQueries({ queryKey: ["gs-status"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/integrations/gsheets/tabs/${id}`)).data,
    onSuccess: () => { toast.success("Tab removed"); qc.invalidateQueries({ queryKey: ["gs-status"] }); },
  });
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="gs-extra-tabs">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Tabs being synced ({tabs.length + 1})</div>
        {!open && <Button size="sm" variant="outline" className="gap-1" onClick={() => setOpen(true)} data-testid="gs-add-tab-btn"><Plus className="w-4 h-4" /> Add another tab</Button>}
      </div>
      <div className="text-xs text-slate-500">Main tab · {isSA ? status.config?.sheet_range : `gid ${status.config?.gid}`} · header row {status.config?.header_row || 1}</div>
      {tabs.map((t) => (
        <div key={t.id} className="flex items-center justify-between text-xs bg-slate-50 dark:bg-slate-900 rounded px-2 py-1.5" data-testid={`gs-tab-${t.id}`}>
          <span><b>{t.label}</b> · {isSA ? t.sheet_range : `gid ${t.gid}`} · header row {t.header_row}</span>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={() => remove.mutate(t.id)} data-testid={`gs-tab-remove-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      {open && (
        <div className="space-y-2 pt-2 border-t">
          <div className="flex gap-2">
            <Input placeholder={isSA ? "Tab name e.g. Sheet2" : "Paste the link of the other tab (has gid=...)"} value={tab} onChange={(e) => setTab(e.target.value)} data-testid="gs-tab-input" />
            <Input type="number" min="1" className="w-20" title="Header row" value={headerRow} onChange={(e) => setHeaderRow(e.target.value)} data-testid="gs-tab-header-row" />
            <Button variant="outline" onClick={doPreview} disabled={busy || !tab} data-testid="gs-tab-preview">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}</Button>
          </div>
          {!isSA && <p className="text-[11px] text-slate-400">Open the second tab in your browser and copy the URL — it ends with #gid=123456.</p>}
          {preview && (
            <>
              <Input placeholder="Label (e.g. Campaign 2)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="gs-tab-label" />
              <div className="text-xs text-slate-500">{preview.total_rows} rows. Map columns:</div>
              <ColumnMapper preview={preview} mapping={mapping} setMapping={setMapping} />
            </>
          )}
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={reset} data-testid="gs-tab-cancel">Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!preview || !mapping.name || add.isPending} onClick={() => add.mutate()} data-testid="gs-tab-save">Add tab</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function GoogleSheetsConnector() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({ queryKey: ["gs-status"], queryFn: async () => (await api.get("/integrations/gsheets/status")).data, refetchInterval: 30000 });
  const [mode, setMode] = useState("service_account");
  const [url, setUrl] = useState("");
  const [tab, setTab] = useState("Sheet1");
  const [headerRow, setHeaderRow] = useState("1");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [pollMinutes, setPollMinutes] = useState(15);
  const [busy, setBusy] = useState(false);

  const connected = status?.connected;
  const connMode = status?.config?.mode || "public_csv";
  const saConfigured = status?.sa_configured;
  const saEmail = status?.sa_email;

  const resetPreview = () => { setPreview(null); setMapping({}); };

  const doPreview = async () => {
    setBusy(true);
    try {
      const ep = mode === "service_account" ? "/integrations/gsheets/sa/preview" : "/integrations/gsheets/preview";
      const payload = mode === "service_account" ? { sheet_url: url, sheet_range: tab, header_row: Number(headerRow) || 1 } : { sheet_url: url, header_row: Number(headerRow) || 1 };
      const { data } = await api.post(ep, payload);
      setPreview(data); setMapping(data.suggested_mapping || {});
      toast.success(`Found ${data.total_rows} rows`);
    } catch (e) { resetPreview(); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const connect = useMutation({
    mutationFn: async () => {
      const ep = mode === "service_account" ? "/integrations/gsheets/sa/connect" : "/integrations/gsheets/connect";
      const payload = mode === "service_account"
        ? { sheet_url: url, sheet_range: tab, mapping, poll_minutes: Number(pollMinutes), header_row: Number(headerRow) || 1 }
        : { sheet_url: url, mapping, poll_minutes: Number(pollMinutes), header_row: Number(headerRow) || 1 };
      return (await api.post(ep, payload)).data;
    },
    onSuccess: () => { toast.success("Connected! Sheet will sync automatically."); resetPreview(); qc.invalidateQueries({ queryKey: ["gs-status"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const syncNow = useMutation({
    mutationFn: async () => (await api.post("/integrations/gsheets/sync-now")).data,
    onSuccess: (d) => { toast.success(`Sync done — ${d.created} new, ${d.merged} updated, ${d.skipped} existing`); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const disconnect = useMutation({
    mutationFn: async () => (await api.post("/integrations/gsheets/disconnect")).data,
    onSuccess: () => { toast.success("Disconnected"); qc.invalidateQueries({ queryKey: ["gs-status"] }); },
  });

  const copyEmail = () => { if (saEmail) { navigator.clipboard.writeText(saEmail); toast.success("Service account email copied"); } };

  return (
    <div className="rounded-xl border bg-white dark:bg-slate-800 p-5" data-testid="gsheets-connector">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 grid place-items-center"><FileSpreadsheet className="w-5 h-5" /></div>
          <div><div className="font-head font-bold">Google Sheets — Live Lead Import</div><div className="text-sm text-slate-500">Auto-pulls new rows into Leads on your chosen interval.</div></div>
        </div>
        {connected
          ? <Badge className="bg-emerald-100 text-emerald-800 gap-1"><CheckCircle2 className="w-3 h-3" /> Connected</Badge>
          : <Badge className="bg-amber-100 text-amber-800 gap-1"><AlertTriangle className="w-3 h-3" /> Setup required</Badge>}
      </div>

      {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : connected ? (
        <div className="space-y-3">
          <div className="text-sm grid grid-cols-2 gap-2">
            <div>Mode: <span className="font-medium">{connMode === "service_account" ? "Service account (private)" : "Public CSV"}</span></div>
            <div>Sheet ID: <span className="font-mono text-xs">{status.config?.sheet_id?.slice(0, 12)}…</span></div>
            <div>{connMode === "service_account" ? "Tab:" : "Tab gid:"} {status.config?.sheet_range || status.config?.gid}</div>
            <div>Header row: {status.config?.header_row || 1}</div>
            <div>Poll every: {status.config?.poll_minutes} min</div>
            <div>Last success: {status.last_success ? new Date(status.last_success).toLocaleString("en-IN") : "—"}</div>
          </div>
          {status.last_error && <div className="text-xs text-red-600 bg-red-50 dark:bg-red-950/40 rounded p-2" data-testid="gs-last-error">Last error: {status.last_error}</div>}
          <ExtraTabs status={status} />
          <p className="text-[11px] text-slate-400">Polling is not instant — the CRM checks the sheet every {status.config?.poll_minutes} min. Rows are de-duplicated by External lead ID or phone, so reordered rows are safe and staff edits are never overwritten.</p>
          <div className="flex gap-2">
            <Button size="sm" className="bg-brand-blue hover:bg-blue-700 gap-1" onClick={() => syncNow.mutate()} disabled={syncNow.isPending} data-testid="gs-sync-now"><RefreshCw className={`w-4 h-4 ${syncNow.isPending ? "animate-spin" : ""}`} /> Sync now</Button>
            <Button size="sm" variant="outline" className="gap-1 text-red-600" onClick={() => disconnect.mutate()} data-testid="gs-disconnect"><Unplug className="w-4 h-4" /> Disconnect</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Mode selector */}
          <div className="flex gap-2" data-testid="gs-mode-tabs">
            <button onClick={() => { setMode("service_account"); resetPreview(); }} data-testid="gs-mode-sa"
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${mode === "service_account" ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700" : "border-slate-200 dark:border-slate-700 text-slate-500"}`}>
              <ShieldCheck className="w-4 h-4" /> Service account (private sheet)
            </button>
            <button onClick={() => { setMode("public_csv"); resetPreview(); }} data-testid="gs-mode-public"
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${mode === "public_csv" ? "border-brand-blue bg-blue-50 dark:bg-blue-950/30 text-brand-blue" : "border-slate-200 dark:border-slate-700 text-slate-500"}`}>
              <Globe className="w-4 h-4" /> Public CSV
            </button>
          </div>

          {mode === "service_account" ? (
            <div className="space-y-3">
              {!saConfigured ? (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-slate-700 dark:text-slate-300 space-y-1" data-testid="gs-sa-not-configured">
                  <b>Service account not configured yet.</b>
                  <ol className="list-decimal list-inside space-y-0.5 mt-1">
                    <li>In Google Cloud Console → IAM & Admin → Service Accounts, create one and enable the <b>Google Sheets API</b>.</li>
                    <li>Create a JSON key for it (revoke any key that was previously shared in chat).</li>
                    <li>Add it to this app as the <b>GOOGLE_SERVICE_ACCOUNT_JSON</b> secret, then reopen this page.</li>
                  </ol>
                </div>
              ) : (
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-3 text-xs text-slate-700 dark:text-slate-300 space-y-2" data-testid="gs-sa-configured">
                  <div><b>Share your private sheet with this service account as Viewer:</b></div>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-[11px] bg-white dark:bg-slate-900 rounded px-2 py-1 flex-1 truncate" data-testid="gs-sa-email">{saEmail}</code>
                    <Button size="sm" variant="outline" className="gap-1" onClick={copyEmail} data-testid="gs-sa-copy"><Copy className="w-3 h-3" /> Copy</Button>
                  </div>
                  <div className="text-slate-500">Open your sheet → Share → paste this email → Viewer → Send. No public sharing needed.</div>
                </div>
              )}
              <div className="flex gap-2">
                <Input placeholder="Paste private Google Sheet URL or ID" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="gs-url" />
                <Input placeholder="Tab name" className="w-32" value={tab} onChange={(e) => setTab(e.target.value)} data-testid="gs-tab" />
                <Input type="number" min="1" className="w-24" title="Header row number" value={headerRow} onChange={(e) => setHeaderRow(e.target.value)} data-testid="gs-header-row" />
                <Button variant="outline" onClick={doPreview} disabled={busy || !url || !saConfigured} data-testid="gs-preview">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}</Button>
              </div>
              <p className="text-[11px] text-slate-400">Header row = which row holds the column titles (usually 1; Meta lead-ads exports often use 2).</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 p-3 text-xs text-slate-600 dark:text-slate-300">
                <b>Before connecting:</b> open your sheet → <b>Share</b> → General access → <b>“Anyone with the link”</b> → <b>Viewer</b>. Then paste the link below. (For private sheets, use the Service account tab.)
              </div>
              <div className="flex gap-2">
                <Input placeholder="Paste Google Sheet URL" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="gs-url" />
                <Input type="number" min="1" className="w-24" title="Header row number" value={headerRow} onChange={(e) => setHeaderRow(e.target.value)} data-testid="gs-header-row" />
                <Button variant="outline" onClick={doPreview} disabled={busy || !url} data-testid="gs-preview">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}</Button>
              </div>
              <p className="text-[11px] text-slate-400">Header row = which row holds the column titles (usually 1; Meta lead-ads exports often use 2).</p>
            </div>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">{preview.total_rows} rows found. Map columns:</div>
              <ColumnMapper preview={preview} mapping={mapping} setMapping={setMapping} />
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Poll every (min)</Label>
                <Input type="number" min="1" className="w-24" value={pollMinutes} onChange={(e) => setPollMinutes(e.target.value)} data-testid="gs-poll" />
                <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1 ml-auto" disabled={!mapping.name || connect.isPending} onClick={() => connect.mutate()} data-testid="gs-connect"><Link2 className="w-4 h-4" /> Connect & Sync</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
